import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import DashboardNavigationListWithFilters from '../DashboardNavigationListWithFilters';
import FiltersSection from '../FiltersSection';

import Logo from '@/components/navbar/Logo';
import { FilterPages } from '@/components/utils/enums';
import { useUser } from '@/hooks/useUser';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { FilterStateSideBar } from '@/interfaces/home/IFilterStateSideBar';
import { AppRoutes } from '@/interfaces/routes/AppRoutes';
import { ConsumerRoutes } from '@/interfaces/routes/ConsumerRoutes';

interface DashboardSidebarPublicProps {
	onFiltersChange?: (filters: FilterStateSideBar) => void;
}

const DashboardSidebarPublic = ({
	onFiltersChange,
}: DashboardSidebarPublicProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useUser();

	const [filters, setFilters] = useState<FilterStateSideBar>({
		productTypes: [],
		vendors: [],
		priceRanges: [],
		statuses: [],
	});

	const [selectedFilterItem, setSelectedFilterItem] =
		useState<FilterPages | null>(null);

	const userIsMerchant =
		user?.roles.includes(UserRole.MerchantDiamond) ||
		user?.roles.includes(UserRole.MerchantRuby) ||
		user?.roles?.includes(UserRole.MerchantSapphire) ||
		user?.roles?.includes(UserRole.MerchantEmerald);

	// Set initial selectedFilterItem based on current route
	useEffect(() => {
		if (location.pathname === '/') {
			if (userIsMerchant) {
				setSelectedFilterItem(FilterPages.HaggleHub);
			} else {
				setSelectedFilterItem(FilterPages.ProductCollections);
			}
		} else {
			setSelectedFilterItem(null);
		}
	}, [location.pathname, userIsMerchant]);

	// Don't show sidebar for consumers and guest users
	if (!user || user.roles.includes(UserRole.Consumer)) {
		return null;
	}

	const handleFiltersUpdate = (newFilters: FilterStateSideBar) => {
		setFilters(newFilters);
		onFiltersChange?.(newFilters);
	};

	const handleNavigationItemClick = (itemLabel: string) => {
		if (
			itemLabel === FilterPages.HaggleHub ||
			itemLabel === FilterPages.ProductCollections
		) {
			setSelectedFilterItem(itemLabel);
		} else {
			setSelectedFilterItem(null);
		}
	};

	return (
		<div
			className="flex flex-col relative p-3 sm:p-4 rounded bg-white gap-3 overflow-auto h-full"
			data-test="public-sidebar"
		>
			<div className="flex justify-center w-full">
				<Logo href={AppRoutes.Landing} width={160} height={28} />
			</div>
			<div className="py-1">
				{userIsMerchant ? (
					<>
						<DashboardNavigationListWithFilters
							filtersContent={
								<FiltersSection
									onFiltersChange={onFiltersChange}
									filters={filters}
									onFiltersUpdate={handleFiltersUpdate}
								/>
							}
							selectedFilterItem={selectedFilterItem}
							onItemClick={handleNavigationItemClick}
						/>
					</>
				) : (
					<>
						<button
							className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 hover:bg-gray-100 text-purpleBrand-500 font-semibold border-l-4 border-purpleBrand-500`}
							onClick={() => {
								setSelectedFilterItem(FilterPages.ProductCollections);
								navigate(ConsumerRoutes.LiveHaggles);
							}}
						>
							<span className="text-sm sm:text-md">
								{FilterPages.ProductCollections}
							</span>
						</button>
						{selectedFilterItem === FilterPages.ProductCollections && (
							<FiltersSection
								onFiltersChange={onFiltersChange}
								filters={filters}
								onFiltersUpdate={handleFiltersUpdate}
							/>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default DashboardSidebarPublic;
